import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useClientUser } from '@/hooks';
import {
    useRoleScope,
    useDeliveryProfiles,
    useDeliveryProfileSelection,
    useProfileSubregionsMutations,
    useCarrierDirectory,
    useShipperDirectory,
    useSubregionImportExport,
} from './hooks';
import { useSubregionState, useSubregionCallbacks } from '../hooks';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { isOnwardAdmin } = useClientUser();
    const scope = useRoleScope();

    const [selected, selectObject] = useState({});
    const [hovered, hoverObject] = useState({});
    const [filter, setFilter] = useState({});
    const [notification, setNotification] = useState({});

    const showError = (message) => setNotification({ severity: 'error', message });
    const showSuccess = (message) => setNotification({ severity: 'success', message });

    const { delivery_profile_id } = useParams();
    // The page stays mounted when the header switches profiles, so one profile's selected subregion
    // and lassoed zips don't carry over to the next.
    useEffect(() => {
        selectObject({});
        hoverObject({});
    }, [delivery_profile_id]);
    // The header's profile dropdown. Same query and variables as the profiles grid, so one cached list.
    const { deliveryProfiles } = useDeliveryProfiles(scope, { onError: showError });
    const profileSelection = useDeliveryProfileSelection(scope, delivery_profile_id, { onError: showError });
    const subregionMutations = useProfileSubregionsMutations(profileSelection.deliveryProfileId, {
        onError: showError,
    });
    const carriers = useCarrierDirectory(scope, profileSelection.deliveryProfileId);

    const shipperDirectory = useShipperDirectory(scope, { onError: showError });

    const subregions = profileSelection.deliveryProfile?.subregions || [];

    const carriersById = useMemo(
        () => Object.fromEntries(carriers.allCarriers.map((c) => [c.client_id, c])),
        [carriers.allCarriers]
    );

    const carrierEnrichments = {
        carriersById,
        mmCarriersById: Object.fromEntries(
            carriers.carriersForLeg('middle_mile_carrier_id').map((c) => [c.client_id, c])
        ),
        warehouseCarriersById: Object.fromEntries(
            carriers
                .carriersForLeg('warehouse_carrier_id')
                .filter((c) => !!c.locations?.length)
                .map((c) => [c.client_id, c])
        ),
    };

    const importExport = useSubregionImportExport({
        subregions,
        deliveryProfile: profileSelection.deliveryProfile,
        deliveryProfileId: profileSelection.deliveryProfileId,
        isOnwardAdmin,
        carriers: carrierEnrichments,
        uploadSubregions: subregionMutations.uploadSubregions,
        onError: showError,
        onSuccess: showSuccess,
    });

    const state = useSubregionState({ subregions, selected, hovered, filter });

    // useSubregionCallbacks is shared with the standalone Subregions page; its mutation half
    // (saveSubregion, createSubregion, deleteSubregion, updateSubregion, importSubregions, addZips,
    // removeZips, removeZip) requires `state.query`/`state.queryVariables` to wire cache updates, which
    // AutoAssign doesn't pass — those mutations are owned by useProfileSubregionsMutations here.
    // Pull only the stateless toggle / naming helpers; never spread the whole object.
    const {
        toggleSubregion,
        toggleZips,
        toggleSubregionHover,
        toggleZipHover,
        setZipHover,
        genName,
        genOwner,
        genColor,
    } = useSubregionCallbacks(
        { subregions },
        // setLoading is unused here — the toggle/naming helpers we destructure don't write
        // loading state. The mutation half of useSubregionCallbacks does, but it's not used:
        // useProfileSubregionsMutations owns mutation state and surfaces it via subregionMutations.loading.
        { setLoading: () => {}, selectObject, hoverObject, setNotification }
    );

    return (
        <Context.Provider
            value={{
                state: {
                    ...state,
                    notification,
                    deliveryProfileId: profileSelection.deliveryProfileId,
                    deliveryProfile: profileSelection.deliveryProfile,
                    deliveryProfiles,
                    profileNotFound: profileSelection.notFound,
                    profileLoadFailed: profileSelection.loadFailed,
                    subregions,
                    scope,
                    shippers: shipperDirectory.shippers,
                    assignmentsByShipper: shipperDirectory.assignmentsByShipper,
                    carriersForLeg: carriers.carriersForLeg,
                    allCarriers: carriers.allCarriers,
                    carriersById,
                },
                loading: {
                    init: profileSelection.loading,
                    subregions: subregionMutations.loading?.subregions,
                    zips: subregionMutations.loading?.zips,
                    // SubregionList reads loading.import to disable the import button + show a
                    // spinner. The actual upload runs through useProfileSubregionsMutations,
                    // whose subregions flag combines insert + delete + upload. Reusing it here
                    // is slightly over-eager (any subregion mutation disables the button) but
                    // keeps the import affordance correct without a separate flag.
                    import: subregionMutations.loading?.subregions,
                },
                callbacks: {
                    toggleSubregion,
                    toggleZips,
                    toggleSubregionHover,
                    toggleZipHover,
                    setZipHover,
                    genName,
                    genOwner,
                    genColor,
                    setNotification,
                    selectObject,
                    hoverObject,
                    setFilter,
                    upsertDeliveryProfile: profileSelection.upsertProfile,
                    createSubregion: subregionMutations.createSubregion,
                    updateSubregion: subregionMutations.updateSubregion,
                    deleteSubregion: subregionMutations.deleteSubregion,
                    addZips: subregionMutations.addZips,
                    removeZips: subregionMutations.removeZips,
                    removeZip: subregionMutations.removeZip,
                    saveSubregion: subregionMutations.saveSubregion,
                    applyToShipper: shipperDirectory.applyToShipper,
                    importSubregions: importExport.importSubregions,
                    exportSubregions: importExport.exportSubregions,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
